import { EVENT_BUFFER_CAPACITY, EVENT_BUFFER_STORAGE_KEY } from '@little-chef/domain';
import type { BufferedEvent } from '@little-chef/domain';

/**
 * EventBuffer
 * ===========
 * FIFO localStorage-backed queue for analytics events when Firebase is unreachable.
 * Capacity: 50 events (oldest evicted on overflow).
 * Storage key: 'telemetry_buffer'
 */
export class EventBuffer {
  private read(): BufferedEvent[] {
    try {
      const raw = localStorage.getItem(EVENT_BUFFER_STORAGE_KEY);
      if (!raw) return [];
      return JSON.parse(raw) as BufferedEvent[];
    } catch {
      return [];
    }
  }

  private write(events: BufferedEvent[]): void {
    try {
      localStorage.setItem(EVENT_BUFFER_STORAGE_KEY, JSON.stringify(events));
    } catch {
      // Storage quota exceeded — fail silently
    }
  }

  enqueue(event: BufferedEvent): void {
    let events = this.read();
    events.push(event);
    // FIFO eviction: remove oldest entries when over capacity
    if (events.length > EVENT_BUFFER_CAPACITY) {
      events = events
        .sort((a, b) => a.createdAt - b.createdAt)
        .slice(events.length - EVENT_BUFFER_CAPACITY);
    }
    this.write(events);
  }

  getAll(): BufferedEvent[] {
    return this.read();
  }

  clear(): void {
    localStorage.removeItem(EVENT_BUFFER_STORAGE_KEY);
  }

  /**
   * Attempt to deliver all buffered events using the provided delivery function.
   * Successfully delivered events are removed. Failed events remain for the next flush.
   */
  async flush(deliver: (event: BufferedEvent) => Promise<void>): Promise<void> {
    const events = this.read();
    if (events.length === 0) return;

    const failed: BufferedEvent[] = [];

    for (const event of events) {
      try {
        await deliver(event);
      } catch {
        failed.push({ ...event, retryCount: event.retryCount + 1 });
      }
    }

    this.write(failed);
  }
}

/** Singleton instance shared across the app. */
export const eventBuffer = new EventBuffer();
